import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  PageContainer,
  Button,
  Badge,
  Select,
  Input,
  DataTable,
  Skeleton,
  EmptyState,
  Pagination,
  ErrorBoundary,
} from '@bbpos/ui';
import type { ColumnDef } from '@bbpos/ui';
import { formatPaise } from '@/utils/currency';
import { useTenantCurrency } from '@/hooks/use-tenant-currency';
import { tenantService } from '@/services/tenant.service';
import { subscriptionService } from '@/services/subscription.service';
import type { Tenant, TenantStatus } from '@/types/tenant';
import {
  GLASS_FILTER,
  GLASS_PANEL,
  cn,
  formatINRDate,
  planTone,
  toneClasses,
} from '@/components/features/subscriptions/design-tokens';

type StatusFilter = '' | TenantStatus;

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: '', label: 'All statuses' },
  { value: 'TRIAL', label: 'Trial' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'SUSPENDED', label: 'Suspended / Grace' },
  { value: 'CANCELLED', label: 'Cancelled' },
  { value: 'CHURNED', label: 'Churned' },
];

const EXPIRING_OPTIONS = [
  { value: '', label: 'Any expiry' },
  { value: '7', label: 'Expiring in 7 days' },
  { value: '14', label: 'Expiring in 14 days' },
  { value: '30', label: 'Expiring in 30 days' },
];

function daysBetween(iso?: string | null): number | null {
  if (!iso) return null;
  return Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000);
}

function KpiCard({
  label,
  value,
  supporting,
  tone,
  icon,
  bars,
}: {
  label: string;
  value: string | number;
  supporting?: string;
  tone: 'success' | 'warning' | 'danger' | 'brand';
  icon: React.ReactNode;
  /** Optional mini-bar values (0–1) to hint at distribution or trend. */
  bars?: number[];
}) {
  const palette = toneClasses(tone);
  return (
    <div
      className={cn(
        GLASS_PANEL,
        'relative overflow-hidden p-5 transition-all duration-200 hover:shadow-lg animate-slide-up',
      )}
    >
      <div className={cn('absolute inset-x-0 top-0 h-14 bg-gradient-to-b', palette.accent)} />
      <div className="relative space-y-3">
        <div className="flex items-start justify-between gap-2">
          <div className={cn('flex h-10 w-10 items-center justify-center rounded-xl', palette.icon)}>
            {icon}
          </div>
          {bars && bars.length > 0 && (
            <div className="flex items-end gap-0.5 h-8" aria-hidden="true">
              {bars.map((v, i) => (
                <span
                  key={i}
                  className={cn('w-1 rounded-full', palette.dot)}
                  // not-money: sparkline bar height as a percentage
                    style={{ height: `${Math.max(10, Math.round(v * 100))}%`, opacity: 0.35 + v * 0.65 }}
                />
              ))}
            </div>
          )}
        </div>
        <div>
          <p className="text-sm font-medium text-neutral-500 dark:text-neutral-400">{label}</p>
          <p className="mt-1 text-2xl font-semibold tracking-tight tabular-nums text-neutral-950 dark:text-neutral-50">
            {value}
          </p>
        </div>
        {supporting && (
          <p className="text-xs text-neutral-500 dark:text-neutral-400 tabular-nums">{supporting}</p>
        )}
      </div>
    </div>
  );
}

function Icon({ d }: { d: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      className="h-5 w-5"
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d={d} />
    </svg>
  );
}

function SubscriptionListContent() {
  const navigate = useNavigate();
  const currency = useTenantCurrency();
  const [searchParams, setSearchParams] = useSearchParams();

  const page = Number(searchParams.get('page') ?? '1');
  const status = (searchParams.get('status') ?? '') as StatusFilter;
  const planName = searchParams.get('plan') ?? '';
  const expiringIn = searchParams.get('expiring') ?? '';
  const search = searchParams.get('search') ?? '';
  const [searchInput, setSearchInput] = useState(search);

  function setParam(key: string, value: string) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value) next.set(key, value);
      else next.delete(key);
      next.set('page', '1');
      return next;
    });
  }

  function commitSearch() {
    setParam('search', searchInput.trim());
  }

  const { data: plans = [] } = useQuery({
    queryKey: ['subscription-plans', 'active'],
    queryFn: () => subscriptionService.listPlans(false),
    staleTime: 60_000,
  });

  const { data: tenantPage, isLoading } = useQuery({
    queryKey: ['tenants', 'subscriptions', status, planName, expiringIn, search, page],
    queryFn: () =>
      tenantService.list({
        page,
        limit: 20,
        status: status || undefined,
        search: search || undefined,
      }),
  });

  const tenants = tenantPage?.data ?? [];

  // Client-side filter by plan + expiring window (backend doesn't expose these
  // filters yet; tenant list payload ships subscription_plan & expires_at).
  const visibleTenants = useMemo(() => {
    return tenants.filter((t) => {
      if (planName && t.subscription_plan !== planName) return false;
      if (expiringIn) {
        const days = daysBetween(t.subscription_expires_at);
        if (days == null || days > Number(expiringIn)) return false;
      }
      return true;
    });
  }, [tenants, planName, expiringIn]);

  // KPI aggregates (from current page — fine for MVP, shows scope in header).
  const kpis = useMemo(() => {
    const planMap = new Map(plans.map((p) => [p.name, p]));
    let mrrPaise = 0;
    let activeCount = 0;
    let trialCount = 0;
    let trialExpiringSoon = 0;
    let graceCount = 0;
    for (const t of tenants) {
      if (t.status === 'ACTIVE') {
        activeCount += 1;
        const plan = planMap.get(t.subscription_plan ?? '');
        if (plan) {
          mrrPaise += plan.price_monthly;
        }
      } else if (t.status === 'TRIAL') {
        trialCount += 1;
        const days = daysBetween(t.subscription_expires_at ?? t.trial_ends_at);
        if (days != null && days <= 7) trialExpiringSoon += 1;
      } else if (t.status === 'SUSPENDED') {
        graceCount += 1;
      }
    }

    // Seven visual "bars" per card — just a textured cue, not a real time series.
    const buildBars = (n: number, max: number) => {
      if (max <= 0) return Array.from({ length: 7 }, () => 0.05);
      const ratio = Math.min(1, n / max);
      return Array.from({ length: 7 }, (_, i) => {
        const base = ratio * (0.4 + 0.6 * Math.sin((i + 1) * 0.9));
        return Math.max(0.1, Math.min(1, base + ratio * 0.3));
      });
    };
    const pageSize = tenants.length || 1;
    const bars = {
      active: buildBars(activeCount, pageSize),
      trial: buildBars(trialCount, pageSize),
      grace: buildBars(graceCount, pageSize),
      mrr: buildBars(mrrPaise, Math.max(1, mrrPaise + 1)),
    };

    return { mrrPaise, activeCount, trialCount, trialExpiringSoon, graceCount, bars };
  }, [tenants, plans]);

  const columns: ColumnDef<Tenant>[] = [
    {
      key: 'business_name',
      header: 'Tenant',
      render: (_v, row) => (
        <div>
          <button
            type="button"
            onClick={() => navigate(`/tenants/${row.id}?tab=subscription`)}
            className="text-left text-sm font-semibold text-neutral-900 dark:text-neutral-100 hover:text-brand-600 dark:hover:text-brand-400"
          >
            {row.business_name}
          </button>
          {row.gstin && (
            <p className="mt-0.5 font-mono text-[11px] text-neutral-500 dark:text-neutral-400">
              {row.gstin}
            </p>
          )}
        </div>
      ),
    },
    {
      key: 'business_type',
      header: 'Vertical',
      render: (_v, row) => (
        <Badge variant={row.business_type === 'RETAIL' ? 'info' : 'warning'} size="sm">
          {row.business_type}
        </Badge>
      ),
    },
    {
      key: 'subscription_plan',
      header: 'Plan',
      render: (_v, row) => {
        const palette = toneClasses(planTone(row.subscription_plan));
        return (
          <span
            className={cn(
              'inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-semibold uppercase tracking-wider',
              palette.pill,
            )}
          >
            <span className={cn('h-1.5 w-1.5 rounded-full', palette.dot)} />
            {row.subscription_plan ?? 'NONE'}
          </span>
        );
      },
    },
    {
      key: 'status',
      header: 'Status',
      render: (_v, row) => {
        const tone =
          row.status === 'ACTIVE'
            ? 'success'
            : row.status === 'TRIAL'
              ? 'info'
              : row.status === 'SUSPENDED'
                ? 'danger'
                : 'neutral';
        const palette = toneClasses(tone);
        return (
          <span className={cn('inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium', palette.pill)}>
            <span className={cn('h-1.5 w-1.5 rounded-full', palette.dot)} />
            {row.status.replace('_', ' ').toLowerCase()}
          </span>
        );
      },
    },
    {
      key: 'subscription_started_at',
      header: 'Started',
      render: (_v, row) => (
        <span className="text-xs tabular-nums text-neutral-600 dark:text-neutral-400">
          {formatINRDate(row.subscription_started_at)}
        </span>
      ),
    },
    {
      key: 'subscription_expires_at',
      header: 'Expires',
      render: (_v, row) => {
        const days = daysBetween(row.subscription_expires_at);
        const tone = days == null ? 'neutral' : days <= 7 ? 'danger' : days <= 30 ? 'warning' : 'success';
        const palette = toneClasses(tone);
        return (
          <div>
            <p className="text-xs tabular-nums text-neutral-600 dark:text-neutral-400">
              {formatINRDate(row.subscription_expires_at)}
            </p>
            {days != null && days >= 0 && (
              <span
                className={cn(
                  'mt-0.5 inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium tabular-nums',
                  palette.pill,
                )}
              >
                {days}d left
              </span>
            )}
          </div>
        );
      },
    },
    {
      key: 'actions',
      header: '',
      render: (_v, row) => (
        <Button variant="ghost" size="sm" onClick={() => navigate(`/tenants/${row.id}?tab=subscription`)}>
          Manage
        </Button>
      ),
    },
  ];

  return (
    <>
      {/* Hero */}
      <div className="relative mb-6 overflow-hidden rounded-2xl bg-gradient-to-br from-indigo-600 to-violet-700 p-6 text-white shadow-[0_20px_60px_-20px_rgba(79,70,229,0.55)] animate-fade-in">
        <div className="pointer-events-none absolute -right-20 -top-24 h-56 w-56 rounded-full bg-white/20 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-16 -left-10 h-48 w-48 rounded-full bg-black/10 blur-3xl" />
        <div className="relative flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="mb-2 inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-xs font-semibold uppercase tracking-wider ring-1 ring-inset ring-white/20">
              <Icon d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              Monetization
            </div>
            <h1 className="text-2xl font-bold text-white">Subscriptions</h1>
            <p className="mt-1 text-indigo-100">
              Every tenant&rsquo;s plan, billing cycle, and revenue contribution at a glance.
            </p>
          </div>
          <Button
            variant="secondary"
            onClick={() => navigate('/subscription-plans')}
            className="border-white/30 bg-white/20 text-white hover:bg-white/30"
          >
            Manage plans
          </Button>
        </div>
      </div>

      {/* KPIs — distribution bars derive from the current page of tenants for
          a quick visual signal; replace with a backend-sourced 30-day series
          once the metrics endpoint ships. */}
      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <KpiCard
          label="Active subscriptions"
          value={kpis.activeCount}
          tone="success"
          icon={<Icon d="M5 13l4 4L19 7" />}
          supporting="on paid plans"
          bars={kpis.bars.active}
        />
        <KpiCard
          label="Trial tenants"
          value={kpis.trialCount}
          tone="brand"
          icon={<Icon d="M12 6v6l4 2m4-2a8 8 0 11-16 0 8 8 0 0116 0z" />}
          supporting={kpis.trialExpiringSoon > 0 ? `${kpis.trialExpiringSoon} expiring in 7 days` : 'all healthy'}
          bars={kpis.bars.trial}
        />
        <KpiCard
          label="Suspended / grace"
          value={kpis.graceCount}
          tone="danger"
          icon={<Icon d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />}
          supporting="need reactivation"
          bars={kpis.bars.grace}
        />
        <KpiCard
          label="MRR on this page"
          value={formatPaise(kpis.mrrPaise, currency)}
          tone="warning"
          icon={<Icon d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8" />}
          supporting="sum of active plan rates"
          bars={kpis.bars.mrr}
        />
      </div>

      {/* Filters */}
      <div className={cn(GLASS_FILTER, 'mb-4 p-4 animate-fade-in')}>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-[1.5fr_1fr_1fr_1fr_auto]">
          <Input
            variant="search"
            placeholder="Search by tenant name or tax ID…"
            value={searchInput}
            onChange={setSearchInput}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commitSearch();
            }}
          />
          <Select
            options={STATUS_OPTIONS}
            value={status}
            onChange={(v) => setParam('status', String(v))}
          />
          <Select
            options={[
              { value: '', label: 'All plans' },
              ...plans.map((p) => ({ value: p.name, label: p.display_name })),
            ]}
            value={planName}
            onChange={(v) => setParam('plan', String(v))}
          />
          <Select
            options={EXPIRING_OPTIONS}
            value={expiringIn}
            onChange={(v) => setParam('expiring', String(v))}
          />
          <Button variant="secondary" size="md" onClick={commitSearch}>
            Apply
          </Button>
        </div>
      </div>

      {/* Table */}
      {isLoading ? (
        <Skeleton variant="rect" height={320} className="rounded-2xl" />
      ) : visibleTenants.length === 0 ? (
        <div className={cn(GLASS_PANEL, 'p-10')}>
          <EmptyState
            variant="section"
            title="No matching subscriptions"
            description="Try widening your filters — different status, plan, or expiry window."
          />
        </div>
      ) : (
        <div className={cn(GLASS_PANEL, 'overflow-hidden')}>
          <DataTable columns={columns} data={visibleTenants} variant="default" />
          {tenantPage?.meta && tenantPage.meta.totalPages > 1 && (
            <div className="flex justify-end border-t border-neutral-100 dark:border-neutral-800/60 p-3">
              <Pagination
                currentPage={tenantPage.meta.page}
                totalPages={tenantPage.meta.totalPages}
                totalItems={tenantPage.meta.total}
                pageSize={tenantPage.meta.limit}
                onPageChange={(p) =>
                  setSearchParams((prev) => {
                    const next = new URLSearchParams(prev);
                    next.set('page', String(p));
                    return next;
                  })
                }
              />
            </div>
          )}
        </div>
      )}
    </>
  );
}

export function SubscriptionListPage() {
  return (
    <ErrorBoundary>
      <PageContainer variant="fluid">
        <SubscriptionListContent />
      </PageContainer>
    </ErrorBoundary>
  );
}
